import { useParams, useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { useMemo, useState } from 'react';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useCheckRun, useDiscardRun, useSaveCheckItem, useMarkItems } from '../lib/queries.js';
import { SyncBanner } from '../components/SyncBanner.js';
import { NOT_ON_PHONE } from '../lib/offline/messages.js';
import { checkRunCsv, downloadText, reportFilename } from '../lib/export.js';
import { actorLabel } from '../lib/people.js';
import type { CheckItemInRun } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { StatusPill } from '../components/StatusPill.js';
import { Icon } from '../components/Icon.js';
import { RunHistory } from '../components/RunHistory.js';
import { areaOf, itemPath } from '../lib/areas.js';
import { TeamChip } from '../components/RosterGrid.js';
import { assetTypeIcon } from '../lib/asset-types.js';
import type { CheckStatus } from '@keetcheck/shared';

// Status breakdown colours for legend
const STATUS_LEGEND: { key: CheckStatus; label: string }[] = [
  { key: 'ok',        label: 'OK'        },
  { key: 'missing',   label: 'Missing'   },
  { key: 'broken',    label: 'Broken'    },
  { key: 'attention', label: 'Attention' },
  { key: 'rectified', label: 'Rectified' },
];

const STATUS_TONE: Record<CheckStatus, keyof typeof theme.status> = {
  ok:        'ok',
  missing:   'danger',
  broken:    'danger',
  attention: 'warn',
  rectified: 'service',
};

export function ChecklistPage() {
  const { runId } = useParams<{ runId: string }>();
  const nav = useNavigate();
  const back = useBack('/checks');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { data, isLoading } = useCheckRun(orgId, runId ?? '');
  const discardMut = useDiscardRun(orgId);
  const saveMut = useSaveCheckItem(orgId, runId ?? '');
  const [filter, setFilter] = useState<CheckStatus | 'flagged' | null>(null);

  // Areas start collapsed so the check opens as a short pick-your-side list.
  // Remembered per check for this tab, so coming back from the wizard keeps it.
  const openKey = `keet.openAreas.${runId}`;
  const [openAreas, setOpenAreas] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(openKey) ?? '[]') as string[]); }
    catch { return new Set(); }
  });
  function setOpen(next: Set<string>) {
    setOpenAreas(next);
    try { sessionStorage.setItem(openKey, JSON.stringify([...next])); } catch { /* private mode */ }
  }
  function toggleArea(a: string) {
    const next = new Set(openAreas);
    next.has(a) ? next.delete(a) : next.add(a);
    setOpen(next);
  }
  const markMut = useMarkItems(orgId, runId ?? '');

  const { sections, checked, total, breakdown, areas } = useMemo(() => {
    const items: CheckItemInRun[] = data?.items ?? [];
    const checked = items.filter((i) => i.status != null).length;
    const total = items.length;

    // build breakdown
    const breakdown: Partial<Record<CheckStatus, number>> = {};
    for (const it of items) {
      if (it.status) breakdown[it.status] = (breakdown[it.status] ?? 0) + 1;
    }

    // group by section; null section → "General"
    const map = new Map<string, { name: string; sortOrder: number; items: CheckItemInRun[] }>();
    const NULL_KEY = '__null__';
    for (const it of items) {
      const key = it.sectionId ?? NULL_KEY;
      if (!map.has(key)) {
        map.set(key, {
          name: it.sectionId ? (it.sectionName ?? 'Section') : 'General',
          sortOrder: it.sectionSortOrder ?? -1,
          items: [],
        });
      }
      map.get(key)!.items.push(it);
    }
    // Items arrive in check order (the asset's section tree), so keep first-seen order.
    const sections = [...map.values()];

    // Areas (drivers side, cabin…) — what one person takes on.
    const areas = new Map<string, { checked: number; total: number; firstOpen?: string; counts: Counts }>();
    for (const it of items) {
      const a = areaOf(it);
      const st = areas.get(a) ?? { checked: 0, total: 0, counts: {} };
      st.total++;
      if (it.status != null) {
        st.checked++;
        st.counts[it.status] = (st.counts[it.status] ?? 0) + 1;
      } else st.firstOpen ??= it.id;
      areas.set(a, st);
    }
    return { sections, checked, total, breakdown, areas };
  }, [data]);

  const remaining = total - checked;
  const flaggedCount = data?.items.filter((i) => i.flagged).length ?? 0;
  // Tap a status in the legend to see just those items.
  const matches = (i: CheckItemInRun) => (filter === 'flagged' ? i.flagged : i.status === filter);
  const shown = filter
    ? sections.map((s) => ({ ...s, items: s.items.filter(matches) })).filter((s) => s.items.length)
    : sections;
  const ready = remaining === 0 && total > 0;
  const isDraft = data?.status === 'draft';
  const firstOpen = data?.items.find((i) => i.status == null);
  const openItem = (id: string) => nav(`/checks/${runId}/items/${id}`);
  // Readings need a value typed in, so they're never ticked from the list.
  const quickOk = (i: CheckItemInRun) => isDraft && i.status == null && i.equipmentKind !== 'reading';

  if (isLoading) return <PageShell noNav><LoadMsg onBack={back}>{navigator.onLine ? 'Loading…' : NOT_ON_PHONE}</LoadMsg></PageShell>;
  if (!data) return <PageShell noNav><LoadMsg onBack={back}>Not found</LoadMsg></PageShell>;

  return (
    <PageShell noNav>
      {/* sticky header */}
      <div style={{
        padding: '14px 18px 14px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        {/* row 1: back · asset name (one line) · exports */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={back} style={iconBtn} aria-label="Back">
            <Icon name="back" size={18} />
          </button>
          <Icon name={assetTypeIcon(data.assetType)} size={20} style={{ color: theme.textDim, flexShrink: 0 }} />
          <div style={{
            flex: 1, minWidth: 0, fontSize: 18, fontWeight: 800, color: theme.text, lineHeight: 1.2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {data.assetName ?? 'Check'}
          </div>
          <button onClick={() => nav(`/checks/${runId}/report?print=1`)} style={exportBtn} title="Report — save as PDF">PDF</button>
          <button onClick={() => downloadText(reportFilename(data, 'csv'), checkRunCsv(data))} style={exportBtn} title="Download CSV">CSV</button>
        </div>

        {/* row 2: who · when · what kind · state */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '10px 0 12px 46px' }}>
          <TeamChip name={data.teamName ?? 'Any crew'} />
          <span style={metaPill}>{fmtDate(data.periodStart)}{data.scheduleId && data.periodEnd !== data.periodStart ? ` – ${fmtDate(data.periodEnd)}` : ''}</span>
          {!data.scheduleId && (
            <span style={{ ...metaPill, background: theme.status.service.bg, color: theme.status.service.fg, border: 'none' }}>On demand</span>
          )}
          {isDraft
            ? <span style={{ ...metaPill, background: theme.status.warn.bg, color: theme.status.warn.fg, border: 'none' }}>In progress</span>
            : <span style={{ ...metaPill, background: theme.status.ok.bg, color: theme.status.ok.fg, border: 'none' }}>Submitted</span>}
        </div>

        {/* progress */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: theme.textDim, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {checked} of {total} checked
          </span>
          <span style={{ fontSize: 12, color: theme.textMuted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.submittedAt
              ? `by ${actorLabel(data.submittedByName, data.submittedByActive)} · ${fmtDate(data.submittedAt)}`
              : `Due ${fmtDate(data.periodEnd)}`}
          </span>
        </div>
        <StatusBar counts={breakdown} total={total} height={8} />

        {/* breakdown legend */}
        {checked > 0 && (
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            {[...STATUS_LEGEND, { key: 'flagged' as const, label: 'Flagged' }].map((s) => {
              const n = s.key === 'flagged' ? flaggedCount : breakdown[s.key];
              if (!n) return null;
              const dot = s.key === 'flagged' ? theme.status.danger.dot : theme.status[STATUS_TONE[s.key]].dot;
              const on = filter === s.key;
              return (
                <button key={s.key} onClick={() => setFilter(on ? null : s.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: on ? 700 : 500, color: on ? theme.text : theme.textDim,
                  padding: '3px 9px', borderRadius: 999, cursor: 'pointer', fontFamily: theme.font,
                  background: on ? theme.surface2 : 'transparent',
                  border: `1px solid ${on ? theme.borderHi : theme.border}`,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: 4, background: dot, flexShrink: 0 }} />
                  {n} {s.label.toLowerCase()}
                </button>
              );
            })}
          </div>
        )}
        {filter && (
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>
            Showing only {filter === 'flagged' ? 'flagged' : filter} items ·{' '}
            <button onClick={() => setFilter(null)} style={{ background: 'none', border: 'none', padding: 0, color: theme.accent, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: theme.font }}>
              Show all
            </button>
          </div>
        )}
      </div>

      <SyncBanner runId={runId ?? ''} />

      {/* item list */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 110, background: theme.surface }}>
        {areas.size > 1 && !filter && (
          <div style={{
            display: 'flex', alignItems: 'center', padding: '8px 18px',
            fontSize: 12, color: theme.textMuted, background: theme.bg,
            borderBottom: `1px solid ${theme.border}`,
          }}>
            <span style={{ flex: 1 }}>{isDraft ? 'Pick your area to start' : `${areas.size} areas`}</span>
            <button
              onClick={() => setOpen(openAreas.size === areas.size ? new Set() : new Set(areas.keys()))}
              style={{ background: 'none', border: 'none', color: theme.textDim, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font }}
            >
              {openAreas.size === areas.size ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
        )}
        {shown.map((sec, si) => {
          const area = areaOf(sec.items[0] ?? {});
          const newArea = si === 0 || areaOf(shown[si - 1]!.items[0] ?? {}) !== area;
          const st = areas.get(area);
          // A single-area check (e.g. Radios) just shows its items; a filter shows every match.
          const isOpen = areas.size <= 1 || !!filter || openAreas.has(area);
          return (
          <div key={sec.name}>
            {newArea && st && (
              <div
                onClick={() => toggleArea(area)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  padding: '14px 18px', background: theme.surface2,
                  borderTop: si ? `1px solid ${theme.border}` : 'none',
                }}
              >
                {areas.size > 1 && (
                  <Icon name={isOpen ? 'chevronD' : 'chevron'} size={16} style={{ color: theme.textMuted, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>{area}</div>
                  <div style={{ fontSize: 12, color: st.checked === st.total ? theme.status.ok.fg : theme.textMuted, fontWeight: 600 }}>
                    {st.checked === st.total ? `Done · ${st.total} item${st.total === 1 ? '' : 's'}` : `${st.checked} of ${st.total} checked`}
                  </div>
                  <div style={{ marginTop: 6, maxWidth: 220 }}>
                    <StatusBar counts={st.counts} total={st.total} height={5} />
                  </div>
                </div>
                {isDraft && st.firstOpen && (
                  <button onClick={(e) => { e.stopPropagation(); nav(itemPath(runId ?? '', st.firstOpen!, area)); }} style={startAreaBtn}>
                    {st.checked ? 'Continue' : 'Start here'} <Icon name="chevron" size={14} />
                  </button>
                )}
              </div>
            )}
            {isOpen && (<>
            {/* section header */}
            <div style={{
              padding: '8px 18px',
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              color: theme.textMuted, textTransform: 'uppercase',
              background: theme.bg, borderBottom: `1px solid ${theme.border}`,
              borderTop: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ flex: 1 }}>{sec.name === area ? 'Items' : sec.name.slice(area.length + 3)}</span>
              {(() => {
                const ids = sec.items.filter(quickOk).map((i) => i.id);
                return ids.length > 1 && (
                  <button
                    onClick={() => markMut.mutate({ itemIds: ids, status: 'ok' })}
                    style={allOkBtn}
                    title="Mark every unchecked item in this section OK"
                  >
                    <Icon name="check" size={12} /> All OK ({ids.length})
                  </button>
                );
              })()}
            </div>
            {sec.items.map((item, i) => (
              <ItemRow
                key={item.id}
                item={item}
                last={i === sec.items.length - 1}
                onClick={() => openItem(item.id)}
                onOk={quickOk(item) ? () => saveMut.mutate({ itemId: item.id, status: 'ok' }) : undefined}
              />
            ))}
            </>)}
          </div>
          );
        })}
        <RunHistory orgId={orgId} runId={runId ?? ''} />
        {/* an on-demand check started by mistake can go — only while untouched */}
        {!data.scheduleId && isDraft && checked === 0 && (
          <div style={{ padding: '14px 18px', textAlign: 'center' }}>
            <button
              onClick={() => discardMut.mutate(data.id, { onSuccess: () => nav('/checks', { replace: true }) })}
              disabled={discardMut.isPending}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: theme.font,
                color: theme.status.danger.fg, fontSize: 13, fontWeight: 600, textDecoration: 'underline',
              }}
            >
              Discard this check
            </button>
          </div>
        )}
      </div>

      {/* sticky CTA */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '12px 16px 32px',
        background: `linear-gradient(to top, ${theme.surface} 70%, transparent)`,
        pointerEvents: 'none',
      }}>
        <button
          disabled={!isDraft || (!ready && !firstOpen)}
          onClick={() => (ready ? nav(`/checks/${runId}/submit`) : firstOpen && openItem(firstOpen.id))}
          style={{
            pointerEvents: 'auto',
            width: '100%', padding: '14px 0',
            background: isDraft ? theme.accent : theme.surface2,
            color: isDraft ? theme.accentInk : theme.textMuted,
            border: `1px solid ${isDraft ? theme.accent : theme.border}`,
            borderRadius: 12, fontSize: 15, fontWeight: 700,
            cursor: isDraft ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontFamily: theme.font,
            transition: 'background .15s',
          }}
        >
          {!isDraft
            ? 'Submitted'
            : ready
              ? <><Icon name="send" size={16} /> Submit for review</>
              : <>{checked === 0 ? 'Start check' : 'Continue'} · {remaining} left <Icon name="chevron" size={16} /></>}
        </button>
      </div>
    </PageShell>
  );
}

function ItemRow({ item, last, onClick, onOk }: {
  item: CheckItemInRun; last: boolean; onClick: () => void;
  /** One-tap OK, offered while the item is still unchecked. */
  onOk?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px',
        borderBottom: last ? 'none' : `1px solid ${theme.border}`,
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15, fontWeight: 600, color: theme.text, lineHeight: 1.3,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {item.equipmentName ?? 'Item'}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
          {expectedLabel(item) && <span>{expectedLabel(item)}</span>}
          {item.reading && <span style={{ color: theme.text, fontWeight: 600 }}>{item.reading}</span>}
          {item.carryForward && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
              background: theme.status.warn.bg, color: theme.status.warn.fg,
            }}>
              CARRY FWD
            </span>
          )}
          {item.note && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Icon name="note" size={11} /> note
            </span>
          )}
          {item.photoCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Icon name="camera" size={11} /> {item.photoCount}
            </span>
          )}
        </div>
      </div>
      {item.resolvedAt && (
        <span title="Marked fixed since this check" style={{
          fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
          background: theme.status.ok.bg, color: theme.status.ok.fg,
        }}>FIXED</span>
      )}
      {item.status
        ? <StatusPill status={item.status as CheckStatus} size="sm" />
        : onOk
          ? <button
              onClick={(e) => { e.stopPropagation(); onOk(); }}
              aria-label={`${item.equipmentName ?? 'Item'} OK`}
              style={{
                height: 34, padding: '0 14px', borderRadius: 999, flexShrink: 0,
                border: `1.5px solid ${theme.status.ok.dot}`, background: theme.status.ok.bg,
                color: theme.status.ok.fg, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                fontFamily: theme.font, display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <Icon name="check" size={14} /> OK
            </button>
          : <span style={{
              width: 16, height: 16, borderRadius: 8,
              border: `1.5px dashed ${theme.borderHi}`,
              flexShrink: 0,
            }} />}
      <Icon name="chevron" size={16} style={{ color: theme.textMuted, flexShrink: 0 }} />
    </div>
  );
}

/** "Qty 8+ each" for items; the target for readings ("1/2 Full"); nothing for plain tasks. */
function expectedLabel(item: CheckItemInRun): string | null {
  const e = item.expected?.trim();
  if (!e || /^check$/i.test(e)) return null;
  if (item.equipmentKind === 'reading') return `Target: ${e}`;
  if (item.equipmentKind === 'task') return e;
  return `Qty ${e}`;
}

type Counts = Partial<Record<CheckStatus, number>>;

/** Progress as coloured segments: OK, rectified, attention, broken, missing; the rest is still to check. */
function StatusBar({ counts, total, height }: { counts: Counts; total: number; height: number }) {
  const order: CheckStatus[] = ['ok', 'rectified', 'attention', 'broken', 'missing'];
  return (
    <div style={{
      display: 'flex', width: '100%', height, background: theme.surface2,
      borderRadius: 999, overflow: 'hidden', border: `1px solid ${theme.border}`,
    }}>
      {total > 0 && order.map((k) => {
        const n = counts[k] ?? 0;
        if (!n) return null;
        return (
          <div key={k} title={`${n} ${k}`} style={{
            width: `${(n / total) * 100}%`, minWidth: 3, height: '100%',
            background: theme.status[STATUS_TONE[k]].dot, transition: 'width .25s',
          }} />
        );
      })}
    </div>
  );
}

/** Loading / not found. The page hides the bottom nav, so keep a way out. */
function LoadMsg({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
      {children}
      <div style={{ marginTop: 16 }}>
        <button onClick={onBack} style={{
          background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10,
          padding: '8px 16px', color: theme.textDim, fontFamily: theme.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>Go back</button>
      </div>
    </div>
  );
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};

const startAreaBtn: React.CSSProperties = {
  height: 34, padding: '0 12px', borderRadius: 10, flexShrink: 0,
  border: 'none', background: theme.accent, color: theme.accentInk,
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
  display: 'inline-flex', alignItems: 'center', gap: 4,
};

const allOkBtn: React.CSSProperties = {
  height: 26, padding: '0 10px', borderRadius: 999, flexShrink: 0,
  border: `1px solid ${theme.status.ok.dot}`, background: theme.status.ok.bg, color: theme.status.ok.fg,
  fontSize: 11, fontWeight: 800, letterSpacing: 0.3, cursor: 'pointer', fontFamily: theme.font,
  display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'none',
};

const metaPill: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
  background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}`,
};

const exportBtn: React.CSSProperties = {
  height: 30, padding: '0 9px', border: `1px solid ${theme.border}`, flexShrink: 0,
  background: theme.surface2, color: theme.textDim, borderRadius: 8,
  fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
};
